import Promise from 'bluebird';
import Task from './Task';
import { isEmpty } from '../../util/array';
import { isNotNumber } from '../../util/numbers';

/**
 * SequentialTaskQueue - a queue of Tasks that can be run in sequence.
 */
export default class SequentialTaskQueue {
  _tasks = null;
  _tasksHaveRun = false;
  _taskDelay = 0;
  _isCancelled = false;

  constructor(taskDelay = 0) {
    if (isNotNumber(taskDelay) || taskDelay < 0) {
      throw new Error('Invalid task batch size');
    }

    this._tasks = new Set();
    this._taskDelay = taskDelay;
  }

  /**
   * addTask - adds a task to the tasks set.
   * It is worth noting that according to the spec, iterating over a set happens in insertion order.
   * @param {Task} task
   */
  addTask(task) {
    if (this._isCancelled) {
      throw new Error('Cannot add tasks after queue is cancelled');
    }

    if (this._tasksHaveRun) {
      throw new Error('Cannot add tasks after queue has run');
    }

    if (!Task.isTask(task)) {
      throw new Error('task must be an instance of class Task');
    }

    if (this._tasks.has(task)) {
      throw new Error('Task already in queue, can only be added once');
    }

    this._tasks.add(task);
  }

  /**
   * run - begins sequentially running tasks, passing the value from the previous task to the next.
   * This method can only be run one time. The first task will have passed to it as a value the
   * SequentialTaskQueue itself.
   * @returns A promise that resolves as the final value from the last Task.
   */
  run() {
    if (this._isCancelled) {
      return Promise.reject(
        new Error('SequentialTaskQueue was already cancelled'),
      );
    }

    if (this._tasksHaveRun) {
      return Promise.reject(
        new Error('SequentialTaskQueue can only be run once'),
      );
    }

    this._tasksHaveRun = true;

    if (this._tasks.size === 0) return Promise.resolve(undefined);

    return [...this._tasks].reduce((promiseChain, task) => {
      return promiseChain.then((value) => {
        return task.run(value, this._taskDelay);
      });
    }, Promise.resolve(this));
  }

  /**
   * cancel - attempts to stops the task queue.
   */
  cancel() {
    this._isCancelled = true;
    if (!this._tasksHaveRun) return;
    if (isEmpty(this._tasks)) return;
    this._tasks.forEach((task) => task.cancel());
  }
}
